import React, { useState, useEffect } from 'react';
import AddDriverModal from '../components/AddDriverModal';
import UpdateDriverModal from '../components/UpdateDriverModal';
import ConfirmationModal from '../components/ConfirmationModal';
import ToastContainer from '../components/ToastContainer';
import { apiService, Driver } from '../services/api';
import { exportDriversToCSV } from '../utils/csvExport';

interface ToastItem {
  id: string;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
}

///hiii

const ManageDrivers: React.FC = () => {
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [driverToDelete, setDriverToDelete] = useState<string | null>(null);
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  // Load drivers on component mount
  useEffect(() => {
    loadDrivers();
  }, []);

  const loadDrivers = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiService.getDrivers();
      console.log('ManageDrivers - Drivers response:', response);
      
      if (response.success && response.data) {
        console.log('ManageDrivers - Full response:', response);
        console.log('ManageDrivers - Response data:', response.data);
        
        // The API response has nested structure: response.data.data contains the drivers array
        const apiResponse = response.data as any;
        const driversArray = apiResponse.data || apiResponse;
        
        console.log('ManageDrivers - Drivers array:', driversArray);
        
        // Log each driver's profile picture
        driversArray.forEach((driver: any, index: number) => {
          console.log(`Driver ${index}:`, {
            _id: driver._id,
            name: driver.name || driver.driverName,
            profilePicture: driver.profilePicture,
            drivingLicense: driver.drivingLicense,
            licenseNumber: driver.licenseNumber,
            fullDriverObject: driver
          });
        });
        
        // Ensure drivers is always an array
        const driversData = Array.isArray(driversArray) ? driversArray : [];
        console.log('ManageDrivers - Setting drivers:', driversData);
        setDrivers(driversData);
      } else {
        setError(response.error || 'Failed to load drivers');
        setDrivers([]); // Set empty array on failure
      }
    } catch (error) {
      console.error('ManageDrivers - Error loading drivers:', error);
      setError('Failed to load drivers');
      setDrivers([]); // Ensure array is set even on error
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddDriver = (newDriver: Driver) => {
    console.log('ManageDrivers - New driver added:', newDriver);
    addToast('Driver added successfully', 'success');
    // Refresh the drivers list to get the latest data from server
    loadDrivers();
  };

  const handleUpdateDriver = (updatedDriver: Driver) => {
    console.log('ManageDrivers - Driver updated:', updatedDriver);
    
    // Update the local state immediately
    setDrivers(prevDrivers => 
      prevDrivers.map(driver => 
        driver._id === updatedDriver._id ? { ...driver, ...updatedDriver } : driver
      )
    );
    
    addToast('Driver updated successfully', 'success');
    
    // Also refresh the drivers list to get the latest data from server
    loadDrivers();
  };

  const handleEditDriver = (driver: Driver) => {
    setSelectedDriver(driver);
    setIsUpdateModalOpen(true);
  };

  const addToast = (message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    const id = Date.now().toString();
    setToasts(prev => [...prev, { id, message, type }]);
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const handleDeleteClick = (id: string) => {
    setDriverToDelete(id);
    setShowConfirmModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!driverToDelete) return;

    try {
      const response = await apiService.deleteDriver(driverToDelete);
      if (response.success) {
        setDrivers(drivers.filter(driver => driver._id !== driverToDelete));
        addToast('Driver deleted successfully', 'success');
      } else {
        addToast(response.error || 'Failed to delete driver', 'error');
      }
    } catch (error) {
      addToast('An unexpected error occurred while deleting the driver', 'error');
    } finally {
      setShowConfirmModal(false);
      setDriverToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowConfirmModal(false);
    setDriverToDelete(null);
  };

  const handleExportDrivers = () => {
    if (drivers.length === 0) {
      addToast('No drivers to export', 'warning');
      return;
    }
    exportDriversToCSV(drivers);
    addToast('Drivers exported successfully', 'success');
  };

  return (
    <div className="p-4 sm:p-6 bg-gray-100 min-h-full">
      {/* Page Title */}

      {/* Action Buttons */}
      <div className="mb-6 flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-3 sm:space-y-0">
      <div className="mb-4">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Manage Driver</h1>
      </div>
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
          <button 
            onClick={() => setIsAddModalOpen(true)}
            className="px-4 py-2 bg-jd-green text-white rounded-lg hover:bg-green-600 transition-colors flex items-center justify-center space-x-2"
          >
            <span>+</span>
            <span>Add Driver</span>
          </button>
          <button 
          onClick={handleExportDrivers}
          className="inline-flex items-center px-4 py-2 bg-blue-100 text-jd-blue rounded-lg hover:bg-blue-200 transition-colors"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Export
        </button>
          <button 
            onClick={() => {
              console.log('Force refresh drivers - Current drivers:', drivers);
              loadDrivers();
            }}
            className="px-4 py-2 bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition-colors"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <div className="text-gray-500">Loading drivers...</div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <div className="text-red-500 mb-4">{error}</div>
          <button 
            onClick={loadDrivers}
            className="px-4 py-2 bg-jd-green text-white rounded-lg hover:bg-green-600 transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {/* Driver Table - Mobile Card View */}
      {!isLoading && !error && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 block sm:hidden">
          {!Array.isArray(drivers) || drivers.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No drivers found. Add your first driver to get started.
            </div>
          ) : (
            drivers.map((driver, index) => {
              console.log(`Rendering driver ${index}:`, {
                _id: driver._id,
                name: driver.driverName || driver.name,
                profilePicture: driver.profilePicture,
                hasProfilePicture: !!driver.profilePicture
              });
              
              return (
              <div key={driver._id || index} className="p-4 border-b border-gray-200 last:border-b-0">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    {driver.profilePicture ? (
                      <img 
                        src={`${driver.profilePicture as string}?t=${Date.now()}`} 
                        alt="Driver Profile" 
                        className="w-16 h-16 rounded-full object-cover border-2 border-gray-200 flex-shrink-0"
                        onError={(e) => {
                          console.log('Mobile view - Image failed to load:', driver.profilePicture);
                          e.currentTarget.style.display = 'none';
                        }}
                        onLoad={() => console.log('Mobile view - Image loaded successfully:', driver.profilePicture)}
                      />
                    ) : (
                      <div className="w-16 h-16 bg-gradient-to-r from-jd-blue to-jd-green rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-lg">
                          {(driver.driverName || driver.name || 'D').charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div>
                      <h3 className="font-medium text-gray-900">{driver.driverName || driver.name}</h3>
                      <p className="text-sm text-gray-500">{driver.mobileNumber || driver.phoneNumber}</p>
                    </div>
                  </div>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    driver.status === 'Active'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {driver.status || 'Active'}
                  </span>
                </div>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">DL No: </span>
                    <span className="text-gray-600">{driver.drivingLicense || driver.licenseNumber || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Address: </span>
                    <span className="text-gray-600">{driver.address}</span>
                  </div>
                  <div className="pt-2">
                    <button 
                      onClick={() => handleEditDriver(driver)}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium mr-4"
                    >
                      Edit
                    </button>
                    <button 
                      onClick={() => driver._id && handleDeleteClick(driver._id)}
                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
              );
            })
          )}
        </div>
      )}

      {/* Driver Table - Desktop View */}
      {!isLoading && !error && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 hidden sm:block">
          {!Array.isArray(drivers) || drivers.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No drivers found. Add your first driver to get started.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Number
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      DL No.
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Address
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {drivers.map((driver, index) => {
                    console.log(`Rendering desktop driver ${index}:`, {
                      _id: driver._id,
                      name: driver.driverName || driver.name,
                      profilePicture: driver.profilePicture,
                      hasProfilePicture: !!driver.profilePicture,
                      drivingLicense: driver.drivingLicense,
                      licenseNumber: driver.licenseNumber,
                      displayLicense: driver.drivingLicense || driver.licenseNumber
                    });
                    
                    return (
                    <tr key={driver._id || index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-3">
                          {driver.profilePicture ? (
                            <img 
                              src={`${driver.profilePicture as string}?t=${Date.now()}`} 
                              alt="Driver Profile" 
                              className="w-12 h-12 rounded-full object-cover border-2 border-gray-200 flex-shrink-0"
                              onError={(e) => {
                                console.log('Desktop view - Image failed to load:', driver.profilePicture);
                                e.currentTarget.style.display = 'none';
                              }}
                              onLoad={() => console.log('Desktop view - Image loaded successfully:', driver.profilePicture)}
                            />
                          ) : (
                            <div className="w-12 h-12 bg-gradient-to-r from-jd-blue to-jd-green rounded-full flex items-center justify-center flex-shrink-0">
                              <span className="text-white font-bold text-sm">
                                {(driver.driverName || driver.name || 'D').charAt(0).toUpperCase()}
                              </span>
                            </div>
                          )}
                          <div className="text-sm font-medium text-gray-900">
                            {driver.driverName || driver.name}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {driver.mobileNumber || driver.phoneNumber}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {driver.drivingLicense || driver.licenseNumber || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {driver.address}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          driver.status === 'Active'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {driver.status || 'Active'}
                        </span>
                      </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button 
                        onClick={() => handleEditDriver(driver)}
                        className="text-blue-600 hover:text-blue-800 mr-4"
                      >
                        Edit
                      </button>
                      <button 
                        onClick={() => driver._id && handleDeleteClick(driver._id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        Delete
                      </button>
                    </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Add Driver Modal */}
      <AddDriverModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onAddDriver={handleAddDriver}
      />

      <UpdateDriverModal
        isOpen={isUpdateModalOpen}
        onClose={() => {
          setIsUpdateModalOpen(false);
          setSelectedDriver(null);
        }}
        onUpdateDriver={handleUpdateDriver}
        driver={selectedDriver}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Driver"
        message="Are you sure you want to delete this driver? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      {/* Toast Container */}
      <ToastContainer
        toasts={toasts}
        onRemoveToast={removeToast}
      />
    </div>
  );
};

export default ManageDrivers;
